import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ScIcon } from './chrome-icons';
import { isCurrent, type ScMenu } from './chrome-nav';

// One desktop header dropdown (Blog design export `.nv` / `.dd`): Offers and Booking open
// the three-column mega panel (Categories · Popular · feature card), Resources a single
// column. The panel is always in the DOM and shown by `is-open`, so the design's fade and
// slide play; while closed it is `visibility: hidden`, which also keeps its links out of
// the tab order.
//
// Pointer: hover opens, leaving closes after 120 ms (so the gap between trigger and panel
// does not flicker it shut). Keyboard: ArrowDown on the trigger opens it and moves into
// the first item, arrows walk the items, Escape closes and returns focus to the trigger,
// and tabbing out closes it. Offers and Booking triggers are links to their main page, so
// a click goes there; Resources is a button that toggles.
export function ChromeMenu({ menu, hot = false }: { menu: ScMenu; hot?: boolean }) {
  const { pathname, search } = useLocation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  // Whether a CLICK opened it, as opposed to hover. Without this the Resources button is
  // a plain toggle, and a mouse user gets: hover opens the menu, then the very click
  // meant to open it closes it again. A click on a hover-opened menu keeps it open.
  const openedByClick = useRef(false);

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);
  useEffect(() => setOpen(false), [pathname, search]);

  useEffect(() => {
    if (!open) return;
    function onDocDown(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  function items(): HTMLAnchorElement[] {
    return Array.from(wrapRef.current?.querySelectorAll<HTMLAnchorElement>('.sc-dd a') ?? []);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape' && open) {
      e.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const list = items();
    const at = list.indexOf(document.activeElement as HTMLAnchorElement);
    if (!open || at < 0) {
      setOpen(true);
      // The panel turns visible on the next frame; focus waits for it.
      requestAnimationFrame(() => items()[0]?.focus());
      return;
    }
    const next = e.key === 'ArrowDown' ? (at + 1) % list.length : (at - 1 + list.length) % list.length;
    list[next]?.focus();
  }

  function onBlur(e: React.FocusEvent) {
    if (!wrapRef.current?.contains(e.relatedTarget as Node | null)) setOpen(false);
  }

  const triggerClass = ['sc-nli', hot ? 'sc-nli--hot' : ''].filter(Boolean).join(' ');
  const triggerBody = (
    <>
      <ScIcon id={menu.icon} className="sc-ni" />
      <span>{menu.label}</span>
    </>
  );

  return (
    <div
      ref={wrapRef}
      className={open ? 'sc-nv is-open' : 'sc-nv'}
      onMouseEnter={() => {
        window.clearTimeout(closeTimer.current);
        setOpen(true);
      }}
      onMouseLeave={() => {
        openedByClick.current = false;
        closeTimer.current = window.setTimeout(() => setOpen(false), 120);
      }}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
    >
      {menu.to ? (
        <Link
          ref={triggerRef as React.RefObject<HTMLAnchorElement>}
          className={triggerClass}
          to={menu.to}
          aria-haspopup="true"
          aria-expanded={open}
          aria-controls={menu.id}
          aria-current={menu.active ? 'page' : undefined}
        >
          {triggerBody}
        </Link>
      ) : (
        <button
          ref={triggerRef as React.RefObject<HTMLButtonElement>}
          type="button"
          className={triggerClass}
          aria-haspopup="true"
          aria-expanded={open}
          aria-controls={menu.id}
          onClick={() => {
            if (open && openedByClick.current) {
              openedByClick.current = false;
              setOpen(false);
              return;
            }
            openedByClick.current = true;
            setOpen(true);
          }}
        >
          {triggerBody}
        </button>
      )}

      <div id={menu.id} className={menu.one ? 'sc-dd sc-dd--one' : 'sc-dd sc-dd--mega'}>
        {menu.groups.map((g, gi) => (
          <div className="sc-col" key={g.title ?? gi}>
            {g.title ? <h5>{g.title}</h5> : null}
            {g.items.map((item) => (
              <Link
                key={item.to + item.label}
                className="sc-di"
                to={item.to}
                aria-current={isCurrent(item.to, pathname, search) ? 'page' : undefined}
                onClick={() => setOpen(false)}
              >
                <i>
                  <ScIcon id={item.icon} />
                </i>
                <span>
                  <b>
                    {item.label}
                    {item.badge ? <span className="sc-bdg sc-bdg--new">{item.badge}</span> : null}
                  </b>
                  <small>{item.sub}</small>
                </span>
              </Link>
            ))}
          </div>
        ))}
        {menu.card ? (
          <Link className="sc-ddf" to={menu.card.to} onClick={() => setOpen(false)}>
            <span className="sc-fe">{menu.card.eyebrow}</span>
            <b>{menu.card.title}</b>
            <small>{menu.card.body}</small>
            <span className="sc-go">
              {menu.card.go}
              <ScIcon id="sc-i-arrow" />
            </span>
          </Link>
        ) : null}
      </div>
    </div>
  );
}
